import * as XLSX from "xlsx";
import {
  TeacherAttendanceSummaryDto,
  TeacherAttendanceResponse,
} from "../services/teacherAttendanceService";

/**
 * Exports teacher monthly attendance summary and detailed logs to an Excel file.
 * 
 * @param summaryList - List of monthly summaries for each teacher
 * @param detailList - List of all daily attendance records for the month
 * @param month - Selected month (1-12)
 * @param year - Selected year
 * @param schoolName - Name of the school
 */
export const exportTeacherAttendanceToExcel = (
  summaryList: TeacherAttendanceSummaryDto[],
  detailList: TeacherAttendanceResponse[],
  month: number,
  year: number,
  schoolName: string = "School"
) => {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const monthStr = monthNames[month - 1] || String(month);

  // 1. Prepare Summary Sheet Data
  const summaryHeaders = [
    "Staff ID",
    "Teacher Name",
    "Month",
    "Year",
    "Days Present",
    "Days Late",
    "Half Days",
    "Incomplete Logs",
    "Days Absent",
    "Leave Days",
    "Total Hours Worked"
  ];

  const summaryRows = summaryList.map((s) => [
    s.staffId,
    s.staffName,
    monthStr,
    s.year,
    s.totalDaysPresent,
    s.totalDaysLate,
    s.totalDaysHalfDay,
    s.totalDaysIncomplete,
    s.totalDaysAbsent,
    s.totalDaysLeave,
    s.totalHoursWorked ? Number(s.totalHoursWorked.toFixed(2)) : 0
  ]);

  // Create worksheet for summary
  const summarySheetData = [summaryHeaders, ...summaryRows];
  const wsSummary = XLSX.utils.aoa_to_sheet(summarySheetData);

  // Apply basic column widths to summary
  wsSummary["!cols"] = [
    { wch: 10 }, // Staff ID
    { wch: 25 }, // Teacher Name
    { wch: 12 }, // Month
    { wch: 8 },  // Year
    { wch: 12 }, // Days Present
    { wch: 10 }, // Days Late
    { wch: 10 }, // Half Days
    { wch: 15 }, // Incomplete Logs
    { wch: 12 }, // Days Absent
    { wch: 12 }, // Leave Days
    { wch: 18 }  // Total Hours Worked
  ];

  // 2. Prepare Detailed Logs Sheet Data
  const detailHeaders = [
    "Date",
    "Staff ID",
    "Teacher Name",
    "Status",
    "Check In Time",
    "Check Out Time",
    "Method",
    "Geo Verified",
    "Hours Worked",
    "GPS Accuracy (m)",
    "Check In Lat",
    "Check In Lng",
    "Check Out Lat",
    "Check Out Lng",
    "Override Reason"
  ];

  const formatTime = (isoString: string | null | undefined): string => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return isoString;
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch (e) {
      return "";
    }
  };

  const detailRows = detailList.map((d) => [
    d.date,
    d.staffId,
    d.staffName,
    d.status,
    formatTime(d.checkInTime),
    formatTime(d.checkOutTime),
    d.checkInMethod,
    d.geoVerified ? "Yes" : "No",
    d.totalHours ? Number(d.totalHours.toFixed(2)) : "",
    d.gpsAccuracyM ? Number(d.gpsAccuracyM.toFixed(1)) : "",
    d.checkInLat || "",
    d.checkInLng || "",
    d.checkOutLat || "",
    d.checkOutLng || "",
    d.overrideNote || ""
  ]);

  // Create worksheet for detailed logs
  const detailSheetData = [detailHeaders, ...detailRows];
  const wsDetail = XLSX.utils.aoa_to_sheet(detailSheetData);

  // Apply basic column widths to detail
  wsDetail["!cols"] = [
    { wch: 12 }, // Date
    { wch: 10 }, // Staff ID
    { wch: 25 }, // Teacher Name
    { wch: 12 }, // Status
    { wch: 15 }, // Check In Time
    { wch: 15 }, // Check Out Time
    { wch: 10 }, // Method
    { wch: 12 }, // Geo Verified
    { wch: 12 }, // Hours Worked
    { wch: 15 }, // GPS Accuracy
    { wch: 12 }, // Check In Lat
    { wch: 12 }, // Check In Lng
    { wch: 12 }, // Check Out Lat
    { wch: 12 }, // Check Out Lng
    { wch: 30 }  // Override Reason
  ];

  // 3. Assemble Workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsSummary, "Monthly Summary");
  XLSX.utils.book_append_sheet(wb, wsDetail, "Detailed Daily Logs");

  // 4. Save file
  const fileName = `${schoolName.replace(/[^a-zA-Z0-9]/g, "_")}_Teacher_Attendance_${monthStr}_${year}.xlsx`;
  XLSX.writeFile(wb, fileName);
};
